import React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { Flex } from 'components/base/layout-components';
import styled from 'styled-components';
import Text from 'components/base/Text';
import TextWithTooltipOnEllipsis from 'components/base/TextWithTooltipOnEllipsis';
import { getColor } from 'common/constants/_colors';
import { CIRCLES_WIDTH } from '../../../pulse.common';
import { WORK_DAYS_OPTIONS } from 'common/constants/constants';

const DateBox = styled(Flex)`
  align-items: center;
  justify-content: center;
  width: ${CIRCLES_WIDTH}rem;
  max-width: ${CIRCLES_WIDTH}rem;
`;

const Bar = styled(Flex)`
  height: 0.5rem;
  width: ${({ width = '100%' }) => width};
  max-width: ${({ width = '100%' }) => width};
  background-color: ${({ color }) => getColor(color)};
`;
const IterationIndicatorWrapper = styled(Flex)`
  background-color: ${({ color }) => getColor(color)};
  border-radius: 0.2rem;
  padding: 0.2rem 0.4rem;
`;

const VerticalLine = styled.div`
  position: absolute;
  z-index: 1;
  left: ${({ index = 0 }) => `${index * CIRCLES_WIDTH}rem`};
  height: ${({ height = 0 }) => `calc(${height} - 100% - 0.5rem)`};
  border-left: ${({ dashed }) =>
    dashed
      ? `1px dashed ${getColor('pigeonPost')};`
      : `1px solid ${getColor('shuttleGray')};`};
`;
const IterationNameWrapper = styled(Flex)`
  padding: 0.5rem 0.7rem;
  margin: 0.4rem 0;
  background-color: ${({ isCurrent }) =>
    getColor(isCurrent ? 'titanWhite' : 'catskillWhite')};
`;
const IterationNameAndTagsWrapper = styled(Flex)`
  flex-direction: column;
  height: 3.5rem;
  justify-content: flex-end;
  align-items: flex-start;
  ${({ showLeftBorder }) =>
    showLeftBorder ? `border-left: 1px dashed ${getColor('pigeonPost')};` : ''};
`;

const IterationIndicator = ({ text, color, transform, style }) => (
  <IterationIndicatorWrapper transform={transform} color={color} style={style}>
    <Text size='x-small' color='white'>
      {text}
    </Text>
  </IterationIndicatorWrapper>
);

const IterationTimeline = ({
  iterations = [],
  tableHeight,
  isIterationFirstDay,
  isIterationLastDay,
  workingDays,
  showTags = true,
  drawTodayLine = true
}) => {
  const resolveCurrentIterationBars = (
    dates,
    isCurrentIterationIsNotLastInLine
  ) => {
    if (isIterationFirstDay) {
      return (
        <>
          <Bar color={'pigeonPost'} width={`${CIRCLES_WIDTH / 2}rem`} />
          <Bar color={'fog'} width={`${CIRCLES_WIDTH}rem`} />{' '}
        </>
      );
    }
    return (
      <>
        <Bar
          color={'chambray'}
          width={`${(dates.length - 2) * CIRCLES_WIDTH + CIRCLES_WIDTH / 2}rem`}
        />
        {isIterationLastDay ? (
          <>
            <Bar color={'chambray'} width={`${CIRCLES_WIDTH}rem`} />
            <Bar color={'fog'} width={`${CIRCLES_WIDTH / 2}rem`} />
          </>
        ) : (
          <Bar
            color={isCurrentIterationIsNotLastInLine ? 'chambray' : 'fog'}
            width={`${2 * CIRCLES_WIDTH - CIRCLES_WIDTH / 2}rem`}
          />
        )}
      </>
    );
  };
  let datesCount = 0;
  return (
    <Flex position='relative' mt='small'>
      {iterations.map((iteration, index) => {
        const { iterationName, isCurrent, dates } = iteration;
        datesCount += dates.length;
        const isLastIteration = index === iterations.length - 1;
        const isCurrentIterationInTheMiddle = isCurrent && !isLastIteration;
        const shouldDrawDashedLine =
          !isLastIteration &&
          isIterationFirstDay !==
            (iterations[index + 1] || { iterationName: '***' }).iterationName &&
          isIterationLastDay !== iterationName;
        return (
          <Flex key={iterationName} direction='column'>
            {showTags && (
              <IterationNameAndTagsWrapper
                showLeftBorder={
                  !(
                    index === 0 ||
                    !iterationName ||
                    iterationName === isIterationFirstDay
                  )
                }
              >
                {iterationName === isIterationFirstDay && (
                  <IterationIndicator color='pastelGreen' text='NEW!' />
                )}
                {iterationName === isIterationLastDay && (
                  <IterationIndicator color='dodgerBlue' text='LAST DAY!' />
                )}
                {!!iterationName && (
                  <IterationNameWrapper isCurrent={isCurrent}>
                    <TextWithTooltipOnEllipsis
                      ellipsisAfter={`${dates.length * CIRCLES_WIDTH - 1.4}rem`}
                      color={isCurrent ? 'black' : 'shuttleGray'}
                    >
                      {iterationName}
                    </TextWithTooltipOnEllipsis>
                  </IterationNameWrapper>
                )}
              </IterationNameAndTagsWrapper>
            )}
            <Flex pt='tiny' pb='tiny'>
              {dates.map(date => {
                const dayOfWeek = moment(date).day() + 1;
                const daysOff = _.get(
                  _.find(
                    WORK_DAYS_OPTIONS,
                    option => option.value === workingDays
                  ),
                  'daysOff',
                  []
                );
                const firstWorkingDay = workingDays.split('-')[0];
                const isDayOff = daysOff.includes(dayOfWeek);

                return (
                  <DateBox key={date}>
                    <Text
                      size='x-small'
                      {...(isDayOff ? { color: 'silverSand' } : {})}
                    >
                      {moment(date)
                        .startOf('day')
                        .isSame(moment().startOf('day'))
                        ? 'TODAY'
                        : dayOfWeek === firstWorkingDay
                        ? moment(dayOfWeek - 1, 'd')
                            .format('ddd')
                            .toUpperCase()
                        : moment(date).format('D')}
                    </Text>
                  </DateBox>
                );
              })}
            </Flex>
            {isCurrent ? (
              <Flex width='100%' pt='tiny'>
                {drawTodayLine && (
                  <VerticalLine height={tableHeight} index={13.5} />
                )}
                {resolveCurrentIterationBars(
                  dates,
                  isCurrentIterationInTheMiddle
                )}
                {isCurrentIterationInTheMiddle && !isIterationLastDay && (
                  <VerticalLine
                    index={datesCount}
                    height={tableHeight}
                    dashed
                  />
                )}
              </Flex>
            ) : (
              <Flex width='100%' pt='tiny'>
                {shouldDrawDashedLine && (
                  <VerticalLine
                    index={datesCount}
                    height={tableHeight}
                    dashed
                  />
                )}
                <Bar color={isIterationLastDay ? 'fog' : 'pigeonPost'} />
              </Flex>
            )}
          </Flex>
        );
      })}
    </Flex>
  );
};

export default IterationTimeline;
